import { useState, useEffect, useRef, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Stack, Play, MagnifyingGlass, MapPin, CheckSquare, Square } from "@phosphor-icons/react";

const MONTHS = [
  ["01", "Jan"], ["02", "Feb"], ["03", "Mär"], ["04", "Apr"], ["05", "Mai"], ["06", "Jun"],
  ["07", "Jul"], ["08", "Aug"], ["09", "Sep"], ["10", "Okt"], ["11", "Nov"], ["12", "Dez"],
];

export default function BulkRecherche() {
  const [bundesland, setBundesland] = useState("bayern");
  const [keyword, setKeyword] = useState("gastronomie");
  const [year, setYear] = useState("2026");
  const [months, setMonths] = useState(new Set(["03"]));

  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [selectedDistricts, setSelectedDistricts] = useState(new Set());
  const [search, setSearch] = useState("");

  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const startPolling = useCallback((jobId) => {
    clearInterval(pollRef.current);
    let failures = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/scrape/jobs/${jobId}`);
        failures = 0;
        setJob(res.data);
        if (res.data.status === "done") {
          clearInterval(pollRef.current);
          toast.success(`Fertig: ${res.data.new} neue Leads, ${res.data.duplicates} Duplikate`);
        } else if (res.data.status === "error") {
          clearInterval(pollRef.current);
          toast.error("Recherche wurde unterbrochen (Server-Neustart). Bitte erneut starten.");
        }
      } catch (e) {
        failures += 1;
        if (failures >= 5) {
          clearInterval(pollRef.current);
          toast.error("Verbindung zum Job verloren. Bitte Seite neu laden.");
        }
      }
    }, 2000);
  }, []);

  // Re-attach to a still-running job after a page reload.
  useEffect(() => {
    api
      .get("/scrape/last-job")
      .then((r) => {
        if (r.data && r.data.status === "running") {
          setJob(r.data);
          startPolling(r.data.id);
        }
      })
      .catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [startPolling]);

  const loadDistricts = async () => {
    if (!bundesland.trim()) return toast.error("Bitte ein Bundesland eingeben");
    setLoadingDistricts(true);
    setDistricts([]);
    setSelectedDistricts(new Set());
    try {
      const { data } = await api.get("/sources/startupservice/landkreise", {
        params: { bundesland, month: `${year}-${[...months][0] || "03"}` },
      });
      setDistricts(data);
      toast.success(`${data.length} Städte/Landkreise geladen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoadingDistricts(false);
    }
  };

  const toggleMonth = (m) =>
    setMonths((prev) => {
      const n = new Set(prev);
      n.has(m) ? n.delete(m) : n.add(m);
      return n;
    });

  const toggleDistrict = (slug) =>
    setSelectedDistricts((prev) => {
      const n = new Set(prev);
      n.has(slug) ? n.delete(slug) : n.add(slug);
      return n;
    });

  const filtered = districts.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectAllFiltered = () =>
    setSelectedDistricts(new Set(filtered.map((d) => d.slug)));
  const clearDistricts = () => setSelectedDistricts(new Set());

  const start = async () => {
    if (months.size === 0) return toast.error("Bitte mindestens einen Monat wählen");
    const monthList = [...months].sort().map((m) => `${year}-${m}`);
    const lks = [...selectedDistricts];
    setStarting(true);
    setJob(null);
    clearInterval(pollRef.current);
    try {
      const { data } = await api.post("/scrape/bulk", {
        bundesland,
        landkreise: lks,
        months: monthList,
        keyword,
      });
      toast.success(`Bulk-Recherche gestartet (${data.total} Abfragen)`);
      startPolling(data.job_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Start fehlgeschlagen");
    } finally {
      setStarting(false);
    }
  };

  const combosCount = (selectedDistricts.size || 1) * months.size;
  const pct = job && job.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Bulk Recherche</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Mehrere Landkreise und Monate auf einmal durchsuchen. Das Tool geht alle Kombinationen
          durch und speichert alle Leads (Duplikate werden übersprungen).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Params */}
        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Bundesland *</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={bundesland} onChange={(e) => setBundesland(e.target.value)} placeholder="z.B. bayern" data-testid="bulk-bundesland" />
              <Button onClick={loadDistricts} disabled={loadingDistricts} data-testid="load-districts-button" variant="outline" className="rounded-sm border-zinc-300 shrink-0">
                <MapPin size={16} className="mr-1" /> {loadingDistricts ? "…" : "Laden"}
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Stichwort</Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="z.B. gastronomie" className="mt-1.5" data-testid="bulk-keyword" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Jahr</Label>
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="mt-1.5 w-28" data-testid="bulk-year" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">Monate *</Label>
            <div className="grid grid-cols-4 gap-1.5">
              {MONTHS.map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => toggleMonth(m)}
                  data-testid={`bulk-month-${m}`}
                  className={`text-xs py-1.5 rounded-sm border transition-colors duration-150 ${
                    months.has(m)
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "border-zinc-200 text-zinc-600 hover:border-[#002FA7]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">
              {selectedDistricts.size === 0
                ? "Kein Landkreis gewählt → gesamtes Bundesland"
                : `${selectedDistricts.size} Landkreise gewählt`}{" "}
              · {months.size} Monate → <span className="font-medium text-[#002FA7]">{combosCount} Abfragen</span>
            </p>
            <Button onClick={start} disabled={starting || months.size === 0} data-testid="bulk-start-button" className="mt-3 w-full bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-11">
              <Play size={16} weight="fill" className="mr-2" />
              {starting ? "Starte…" : "Bulk Recherche starten"}
            </Button>
          </div>
        </div>

        {/* Districts */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold">Städte & Landkreise</h2>
            {districts.length > 0 && (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={selectAllFiltered} data-testid="districts-select-all" className="h-8 text-xs">
                  <CheckSquare size={14} className="mr-1" /> Alle
                </Button>
                <Button size="sm" variant="ghost" onClick={clearDistricts} data-testid="districts-clear" className="h-8 text-xs">
                  <Square size={14} className="mr-1" /> Keine
                </Button>
              </div>
            )}
          </div>

          {districts.length === 0 ? (
            <div className="text-sm text-zinc-400 py-12 text-center border border-dashed border-zinc-200 rounded-sm">
              Lade zuerst ein Bundesland, um Landkreise auszuwählen.
            </div>
          ) : (
            <>
              <Input
                placeholder="Filtern…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-3 h-9"
                data-testid="districts-search"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[420px] overflow-y-auto pr-1" data-testid="districts-list">
                {filtered.map((d) => (
                  <label
                    key={d.slug}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer text-sm hover:bg-zinc-50 ${
                      selectedDistricts.has(d.slug) ? "bg-[#002FA7]/5" : ""
                    }`}
                    data-testid={`district-${d.slug}`}
                  >
                    <Checkbox
                      checked={selectedDistricts.has(d.slug)}
                      onCheckedChange={() => toggleDistrict(d.slug)}
                    />
                    <span className="flex-1 truncate">{d.name}</span>
                    <span className="text-[10px] text-zinc-400 uppercase">{d.type === "Stadt" ? "Stadt" : "LK"}</span>
                    <span className="text-[10px] text-zinc-400 tabular-nums">{d.count}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Job progress */}
      {job && (
        <div className="mt-8 bg-white border border-zinc-200 rounded-md p-6" data-testid="bulk-progress">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
              <Stack size={18} className="text-[#002FA7]" />
              {job.status === "done" ? "Abgeschlossen" : job.status === "error" ? "Unterbrochen" : "Läuft…"}
            </h2>
            <span className="text-sm text-zinc-500">{job.done} / {job.total} Abfragen</span>
          </div>
          <Progress value={pct} className="h-2" />
          {job.current && job.status !== "done" && (
            <p className="text-xs text-zinc-400 mt-2">Aktuell: {job.current}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-4">
            <Stat label="Gefunden" value={job.found} />
            <Stat label="Neu" value={job.new} accent />
            <Stat label="Duplikate" value={job.duplicates} muted />
          </div>
          {job.errors && job.errors.length > 0 && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
              <div className="font-medium mb-1">{job.errors.length} Fehler:</div>
              {job.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {/* Found leads + duplicates */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <div data-testid="bulk-new-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Neue Leads ({job.new ?? (job.new_leads || []).length})
                {(job.new ?? 0) > (job.new_leads || []).length && (
                  <span className="normal-case tracking-normal text-zinc-400"> · letzte {(job.new_leads || []).length} angezeigt</span>
                )}
              </div>
              <div className="border border-zinc-200 rounded-sm max-h-72 overflow-y-auto">
                {(job.new_leads || []).length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Noch keine neuen Leads.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {(job.new_leads || []).map((l, i) => (
                        <tr key={i} className="border-b border-zinc-100 last:border-0">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">#{String(l.lead_no ?? "").padStart(4, "0")}</td>
                          <td className="px-2 py-1.5 font-medium">{l.name}</td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{l.plz} {l.city}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div data-testid="bulk-duplicate-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Duplikate ({job.duplicates ?? (job.duplicate_leads || []).length})
                {(job.duplicates ?? 0) > (job.duplicate_leads || []).length && (
                  <span className="normal-case tracking-normal text-zinc-400"> · letzte {(job.duplicate_leads || []).length} angezeigt</span>
                )}
              </div>
              <div className="border border-zinc-200 rounded-sm max-h-72 overflow-y-auto">
                {(job.duplicate_leads || []).length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Keine Duplikate.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {(job.duplicate_leads || []).map((l, i) => (
                        <tr key={i} className="border-b border-zinc-100 last:border-0">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">
                            {l.existing_lead_no ? `#${String(l.existing_lead_no).padStart(4, "0")}` : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-700">{l.name}</td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{l.plz} {l.city}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div className={`px-5 py-3 rounded-md border ${accent ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 bg-white"}`}>
      <div className={`font-heading text-2xl font-bold ${accent ? "text-[#002FA7]" : muted ? "text-zinc-400" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
